/**
 * Metadados VISUAIS dos nós de Marketing nas automações (issue #150, Fase 1: leitura).
 *
 * Os campos e rótulos vêm do catálogo do backend (api/automations/marketing_nodes.py);
 * aqui ficam só o ícone/tom e o resumo de uma linha do card. Separado de nodeMeta.js
 * (que importa este arquivo) para não misturar com os demais módulos.
 */

export const MARKETING_NODE_META = {
  'trigger.ad_lead_created': { icon: 'megaphone' },
  'trigger.lead_ad_form_received': { icon: 'megaphone' },
  'trigger.lead_funnel_stage_changed': { icon: 'trend_up' },
  'trigger.marketing_conversion': { icon: 'target' },
  'trigger.campaign_status_changed': { icon: 'status' },
  'trigger.campaign_budget_reached': { icon: 'wallet' },
  'trigger.ads_pace_above_goal': { icon: 'timer' },
  'trigger.campaign_metric_crossed': { icon: 'chart' },
  'trigger.keyword_alert': { icon: 'search' },
  'trigger.ads_sync_failed': { icon: 'refresh' },
  'trigger.ads_account_disconnected': { icon: 'alert' },
  'trigger.google_ads_change': { icon: 'edit' },
  'trigger.organic_traffic_drop': { icon: 'trend_dn' },
  'action.lead_ads_create_contact': { icon: 'leads', tone: 'green' },
  'action.marketing_notify': { icon: 'bell' },
  'action.marketing_create_optimization': { icon: 'bulb' },
  'action.marketing_tag_lead': { icon: 'tag', tone: 'green' },
  'action.marketing_resync': { icon: 'refresh' },
}

const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

const ORIGEM = { google: 'Google Ads', meta: 'Meta Ads', organico: 'orgânico' }
const PLATAFORMA = { google: 'Google Ads', meta: 'Meta Ads', todas: 'Google e Meta' }
const ESTAGIO = {
  visitantes: 'visitantes', whatsapp: 'WhatsApp', formularios: 'formulários',
  leads: 'leads', qualificado: 'qualificado', venda: 'venda',
}
const METRICA = {
  cpa: 'CPA', cpc: 'CPC', ctr: 'CTR', gasto_sem_conversao: 'Gasto sem conversão',
  frequencia: 'Frequência', roas: 'ROAS',
}

/** Sufixo de filtro dos gatilhos de lead: " · Google Ads · black-friday". */
function filtrosLead(config) {
  const partes = [config.origem ? ORIGEM[config.origem] : null, config.campanha || null].filter(Boolean)
  return partes.length ? ` · ${partes.join(' · ')}` : ''
}

/** Sufixo de filtro dos gatilhos de campanha: " · Meta Ads · remarketing". */
function filtrosCampanha(config) {
  const partes = [config.plataforma ? PLATAFORMA[config.plataforma] : null, config.campanha || null].filter(Boolean)
  return partes.length ? ` · ${partes.join(' · ')}` : ''
}

/**
 * Resumo de uma linha dos nós de Marketing. Devolve `undefined` para tipos que não são
 * de Marketing (quem chama segue para o resumo dos demais nós).
 */
export function summarizeMarketing(key, config = {}) {
  switch (key) {
    case 'trigger.ad_lead_created':
      return `Lead de anúncio criado${filtrosLead(config)}`
    case 'trigger.lead_ad_form_received':
      return config.form_id ? 'Formulário escolhido' : 'Qualquer formulário de Lead Ads'
    case 'action.lead_ads_create_contact':
      return config.status ? `Cria o contato (${config.status})` : 'Cria o contato do formulário'
    case 'trigger.lead_funnel_stage_changed': {
      const de = config.de_estagio ? ` de ${ESTAGIO[config.de_estagio]}` : ''
      const para = config.para_estagio ? ` para ${ESTAGIO[config.para_estagio]}` : ''
      const avanco = de || para ? `Avançou${de}${para}` : 'Qualquer avanço no funil'
      return `${avanco}${filtrosLead(config)}`
    }
    case 'trigger.marketing_conversion': {
      const evento = { qualificado: 'Lead qualificado', venda: 'Venda' }[config.evento] || 'Qualificado ou venda'
      return `${evento}${filtrosLead(config)}`
    }
    case 'trigger.campaign_status_changed': {
      const mudanca = { pausada: 'Campanha pausada', reativada: 'Campanha reativada' }[config.mudanca]
      return `${mudanca || 'Campanha pausada ou reativada'}${filtrosCampanha(config)}`
    }
    case 'trigger.campaign_budget_reached':
      return `Gasto ≥ ${config.percentual || 100}% do orçamento diário${filtrosCampanha(config)}`
    case 'trigger.ads_pace_above_goal':
      return config.meta_gasto ? `Projeção do mês > R$ ${config.meta_gasto}` : 'Informe a meta de gasto'
    case 'trigger.campaign_metric_crossed': {
      if (!config.metrica || config.limite == null || config.limite === '') return 'Configure a métrica e o limite'
      const op = config.operador === 'below' ? '<' : '>'
      return `${METRICA[config.metrica]} ${op} ${config.limite} (${config.janela_dias || '?'} dias)${filtrosCampanha(config)}`
    }
    case 'trigger.keyword_alert':
      if (config.tipo === 'sem_conversao') {
        return config.gasto_minimo ? `Palavra-chave com R$ ${config.gasto_minimo}+ sem conversão` : 'Informe o gasto mínimo'
      }
      return config.tipo === 'quality_score' && config.quality_score_max
        ? `Quality Score ≤ ${config.quality_score_max}`
        : 'Escolha o alerta'
    case 'trigger.ads_sync_failed':
      return `Sync de anúncios falhou${config.plataforma ? ` · ${PLATAFORMA[config.plataforma]}` : ''}`
    case 'trigger.ads_account_disconnected':
      return `Conta desconectada${config.plataforma ? ` · ${PLATAFORMA[config.plataforma]}` : ''}`
    case 'trigger.google_ads_change':
      return `Alteração no Google Ads${config.recurso ? ` · ${trunc(config.recurso, 24)}` : ''}${config.usuario ? ` · ${trunc(config.usuario, 24)}` : ''}`
    case 'trigger.organic_traffic_drop':
      return config.queda_percentual && config.janela_dias
        ? `Orgânico cai ${config.queda_percentual}%+ em ${config.janela_dias} dias`
        : 'Informe a queda e a janela'
    case 'action.marketing_notify':
      if (config.canal === 'whatsapp') return config.numeros ? `WhatsApp para ${trunc(config.numeros, 32)}` : 'Informe os números'
      return config.canal === 'interno' ? 'Notificação interna com resumo da campanha' : 'Escolha o canal'
    case 'action.marketing_create_optimization':
      return config.titulo ? trunc(config.titulo, 44) : 'Atividade “Otimizar campanha”'
    case 'action.marketing_tag_lead':
      return config.tag ? `Tag ${trunc(config.tag, 30)}` : 'Tag “campanha: <nome>”'
    case 'action.marketing_resync':
      return `Re-sync ${PLATAFORMA[config.plataforma] || 'da plataforma do gatilho'}`
    default:
      return undefined
  }
}
